<template>
    <flex-container :gap="Size.lg">
        <flex-container justify-content="space-between" align-items="center">
            <flex-container size="auto" :gap="Size.xs">
                <text-heading>
                    {{ documentType.label }} - {{ lead.pipeline.name }} - {{ lead.primaryContact.name }}
                </text-heading>
            </flex-container>

            <flex-container
                v-if="!skipGenerateConceptPage"
                size="auto"
                justify-content="flex-end"
            >
                <button-outline @click="backToLeadDetail">
                    Annuleren
                </button-outline>

                <button-block :disabled="!templateVariablesReplaced || !commandIsValid" @loading="generateDocument">
                    {{ documentType.label }} genereren
                </button-block>
            </flex-container>
        </flex-container>
    </flex-container>

    <horizontal-line />

    <flex-container>
        <flex-container v-if="!templateVariablesReplaced" :size="5">
            <text-heading>
                Instellingen
            </text-heading>
            <card-normal>
                <flex-container :gap="Size.lg">
                    <flex-container :gap="Size.xl">
                        <flex-container :size="5">
                            <input-group v-model="template">
                                <template #label>
                                    <input-label>
                                        Template
                                    </input-label>
                                </template>
                                <template #input>
                                    <select-input
                                        :items="availableTemplates"
                                        value-key-name="id"
                                        label-key-name="name"
                                    />
                                </template>
                            </input-group>
                        </flex-container>

                        <flex-container :size="5">
                            <input-group v-model="team">
                                <template #label>
                                    <input-label>
                                        Afzender
                                    </input-label>
                                </template>
                                <template #input>
                                    <team-select-input
                                        :teams="availableTeams"
                                    />
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>

                    <text-heading sub>
                        We hebben enkele antwoorden nodig om het document te maken.
                    </text-heading>

                    <div class="template-variables">
                        <div
                            v-for="templateVariable in templateVariables"
                            :key="templateVariable.key"
                        >
                            <input-group v-model="templateVariable.value">
                                <template #label>
                                    <input-label>
                                        {{ templateVariable.label }}
                                        <text-decorator
                                            v-if="templateVariable.sublabel"
                                            :size="FontSize.xs"
                                        >
                                            ({{ templateVariable.sublabel }})
                                        </text-decorator>
                                    </input-label>
                                </template>
                                <template #input>
                                    <text-input />
                                </template>
                            </input-group>
                        </div>
                    </div>

                    <flex-container justify-content="flex-end">
                        <button-outline @click="backToLeadDetail">
                            Annuleren
                        </button-outline>

                        <button-block
                            v-if="!skipGenerateConceptPage"
                            :disabled="!templateVariablesAreValid"
                            @loading="generateText"
                        >
                            Maak concept
                        </button-block>

                        <button-block
                            v-else
                            :disabled="!templateVariablesAreValid || !commandIsValid"
                            @loading="generateDocument"
                        >
                            {{ documentType.label }} genereren
                        </button-block>
                    </flex-container>
                </flex-container>
            </card-normal>
        </flex-container>

        <flex-container v-else :size="5">
            <card-normal>
                <flex-container>
                    <flex-container align-items="center" justify-content="space-between">
                        <text-heading>
                            {{ documentType.label }} bewerken
                        </text-heading>

                        <button-outline @click="templateVariablesReplaced = false">
                            Vorige
                        </button-outline>
                    </flex-container>

                    <flex-container>
                        <input-group v-model="command.payload.text">
                            <template #input>
                                <rich-text-area-input
                                    size="xl"
                                    toolbar="extended"
                                    :mentionables="[]"
                                />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
            </card-normal>
        </flex-container>

        <flex-container :size="10" style="flex: 0 0 640px;">
            <card-normal>
                <preview-iframe :srcdoc="preview" :size="template.layout?.size" />
            </card-normal>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import {
        ButtonState,
        type ButtonStateChangedEvent,
        CardNormal,
        FlexContainer,
        FontSize,
        HorizontalLine,
        Size,
        TextHeading,
        TextDecorator,
    } from '@keyvue/one-ui';

    import PreviewIframe from '@views/Document/Template/Detail/PreviewIframe.vue';
    import TeamSelectInput from '@components/Inputs/TeamSelectInput.vue';

    import {
        CreateDocumentCommand,
        PreviewDocumentCommand,
        DocumentTemplate,
        DocumentType,
        Team,
        Lead,
    } from '@keyvue/one-api-client';

    import { computed, reactive, ref, toRef, unref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';
    import useScalarSelectInput from '@composables/useScalarSelectInput';
    import lodash from 'lodash';

    interface Props {
        type: string;
        leadId: string;
    }

    const props = defineProps<Props>();
    const router = useRouter();
    const { client, currentUser, getReactiveCollection } = useClient();
    const { notifySaving, notifySaved, notifyError } = useNotify();

    if (!currentUser.value) throw new Error('No user found');

    const documentType = ref(new DocumentType(props.type));

    const lead = reactive(await client.get(Lead, props.leadId)) as Lead;

    const initCommand = () => {
        const command = CreateDocumentCommand.create();

        command.leadId = props.leadId;
        command.payload = {
            text: '',
        };
        command.createdById = currentUser.value?.id.value ?? '';

        return command;
    };

    const command = reactive(initCommand()) as CreateDocumentCommand;
    const commandIsValid = computed(() => command.validationSchema.isValidSync(command));

    const templateCollection = await getReactiveCollection(DocumentTemplate, { active: true, 'layout.type': documentType.value, 'order[name]': 'asc' });
    const availableTemplates = computed(() => templateCollection.data.value);
    const template = useScalarSelectInput(toRef(command, 'templateId'), availableTemplates.value, 'id');
    if (!template.value && availableTemplates.value.length) {
        template.value = availableTemplates.value[0].id;
    }

    watch(template, () => {
        command.payload.text = template.value.payload.text;
    }, { immediate: true });

    const teamCollection = await client.getSubResources(currentUser.value, Team, 'teams');
    const availableTeams = computed(() => teamCollection.data);
    const team = useScalarSelectInput(toRef(command, 'teamId'), availableTeams.value, 'id');
    if (!team.value && availableTeams.value.length) {
        team.value = availableTeams.value[0].id;
    }

    interface TemplateVariable {
        key: string,
        label: string,
        sublabel?: string,
        value: string,
    }

    const templateVariables = ref<TemplateVariable[]>([]);
    const templateVariablesReplaced = ref(false);

    const templateVariablesAreValid = computed(() => {
        let valid = true;

        templateVariables.value.forEach((templateVariable) => {
            if (!templateVariable.value) {
                valid = false;
            }
        });

        return valid;
    });

    const templateVariableValues = computed(() => {
        const values = {};

        templateVariables.value.forEach((templateVariable) => {
            values[templateVariable.key] = templateVariable.value;
        });

        return values;
    });

    const autoTemplateVariableValues = computed(() => ({
        relatie_naam: lead.primaryContact.name.fullName,
        relatie_voornaam: lead.primaryContact.name.firstName,
        relatie_achternaam: lead.primaryContact.name.fullLastName,
        relatie_aanhef_kort: lead.primaryContact.gender?.shortSalutation ?? 'dhr./mevr.',
        relatie_aanhef_lang: lead.primaryContact.gender?.longSalutation ?? 'heer/mevrouw',
        relatie_adres: lead.primaryContact.address?.fullStreet,
        relatie_postcode: lead.primaryContact.address?.postalCode,
        relatie_plaats: lead.primaryContact.address?.city,
        relatie_telefoonnummer: lead.primaryContact.phoneNumber ?? lead.primaryContact.mobilePhoneNumber,
        relatie_email: lead.primaryContact.email,

        team_naam: team.value.displayName,
        team_adres: team.value.address?.fullStreet ?? '',
        team_postcode: team.value.address?.postalCode ?? '',
        team_plaats: team.value.address?.city ?? '',
        team_telefoonnummer: team.value.phoneNumber ?? '',
        team_email: team.value.email,

        medewerker_naam: currentUser.value?.name.fullName,
        medewerker_voornaam: currentUser.value?.name.firstName,
        medewerker_achternaam: currentUser.value?.name.fullLastName,
    }));

    const templateVariableSublabelRegex = '\\((?<sublabel>.*?)\\)';
    const templateVariableLabelRegex = `\\{(?<label>.*?)( ?${templateVariableSublabelRegex})?\\}`;
    const templateVariableKeyRegex = '%(?<key>[a-zA-Z_][a-zA-Z_0-9.-]*)%';
    const skipGenerateConceptPage = computed(() => !template.value.layout.editable);

    const parseTemplateVariables = () => {
        const values = unref(templateVariableValues);
        const autoValues = unref(autoTemplateVariableValues);

        templateVariables.value.splice(0);

        const regex = new RegExp(`${templateVariableKeyRegex}(${templateVariableLabelRegex})?`, 'gi');

        const matches = [...template.value.payload.text.matchAll(regex)];
        matches.forEach((match) => {
            // Check if key exists in auto variables
            if (match.groups.key in autoValues) {
                return;
            }

            // Check if key exists
            if (templateVariables.value.some((templateVariable) => templateVariable.key === match.groups.key)) {
                return;
            }

            // Add variable
            templateVariables.value.push({
                key: match.groups.key,
                label: match.groups.label ?? match.groups.key,
                sublabel: match.groups.sublabel ?? '',
                value: match.groups.key in values ? values[match.groups.key] : '',
            });
        });

        if (skipGenerateConceptPage.value) {
            templateVariables.value.push({
                key: 'aanhef',
                label: 'Aanhef',
                value: lead.primaryContact.gender?.longSalutation ?? '',
            });

            templateVariables.value.push({
                key: 'achternaam',
                label: 'Achternaam',
                value: lead.primaryContact.name?.fullLastName ?? '',
            });

            templateVariables.value.push({
                key: 'straatnaam',
                label: 'Straatnaam',
                value: lead.primaryContact.address?.street ?? '',
            });

            templateVariables.value.push({
                key: 'huisnummer',
                label: 'Huisnummer',
                value: [
                    lead.primaryContact.address?.number ?? '',
                    lead.primaryContact.address?.numberSuffix ?? '',
                ].join(' '),
            });
        }
    };

    const replaceTemplateVariables = (text: string) => {
        let replacedText = text;

        const values = {
            ...unref(templateVariableValues),
            ...unref(autoTemplateVariableValues),
        };

        Object.keys(values).forEach((key) => {
            const regex = new RegExp(`%${key}%(${templateVariableLabelRegex})?`, 'gi');

            replacedText = replacedText.replace(regex, values[key] ?? '');
        });

        return replacedText;
    };

    watch(template, parseTemplateVariables, { immediate: true });

    const generateText = () => {
        command.payload.text = replaceTemplateVariables(template.value.payload.text);
        templateVariablesReplaced.value = true;
    };

    const backToLister = () => {
        router.push({ name: 'documentList' });
    };

    const backToLeadDetail = () => {
        router.push({
            name: 'contactLeadDetail',
            params: {
                contactId: lead.primaryContact.id.toString(),
                leadId: lead.id.toString(),
            },
        });
    };

    const generateDocument = async (event: ButtonStateChangedEvent) => {
        try {
            notifySaving();
            await client.dispatch(command);
            notifySaved();
            backToLister();
        } catch (error) {
            notifyError(error);
        } finally {
            event.button.setState(ButtonState.completed);
        }
    };

    const preview = ref('');

    const previewCommand = computed(() => {
        const result = PreviewDocumentCommand.create();
        result.templateId = command.templateId;
        result.teamId = command.teamId;
        result.leadId = command.leadId;
        result.payload = command.payload;

        templateVariables.value.forEach((templateVariable) => {
            result.payload[templateVariable.key] = templateVariable.value;
        });

        return result;
    });
    const previewCommandIsValid = computed(() => previewCommand.value.validationSchema.isValidSync(previewCommand.value));

    const refreshPreview = async () => {
        if (!previewCommandIsValid.value) {
            return;
        }

        preview.value = await client.dispatch(previewCommand.value);
    };

    watch(previewCommand, lodash.debounce(refreshPreview, 400, { maxWait: 2000 }), { immediate: true, deep: true });
</script>

<style scoped lang="scss">
.template-variables {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: size(md);
    row-gap: size(md);
}
</style>
